import { UploadStrategy, UploadStrategyConfig } from './strategy/upload-strategy';
import { UploadedFile } from '@ui/utils/files/uploaded-file';
import { Disk } from './backend-metadata';
import { S3MultipartUpload } from './strategy/s3-multipart-upload';
import { S3Upload } from './strategy/s3-upload';
import { TusUpload } from './strategy/tus-upload';
import { AxiosUpload } from './strategy/axios-upload';
import { FileUpload, FileUploadState } from './file-upload-store';
import { validateFile } from '@ui/utils/files/validate-file';
import { getBootstrapData } from '@ui/bootstrap-data/bootstrap-data-store';
import { toast } from '@ui/toast/toast';
import { ProgressTimeout } from './progress-timeout';
import { message } from '@ui/i18n/message';

const AUTO_RETRY_DELAY = 10000; // 10 seconds

export async function startUploading(
  upload: FileUpload,
  state: FileUploadState,
): Promise<UploadStrategy | null> {
  const sessionId = (state as any).getUploadSessionId ? (state as any).getUploadSessionId() : null;
  const settings = getBootstrapData().settings;
  const options = upload.options;
  const file = upload.file as UploadedFile;

  // validate file, if validation fails, error the upload and bail
  if (options?.restrictions) {
    const errorMessage = validateFile(file, options.restrictions);
    if (errorMessage) {
      state.updateFileUpload(file.id, {
        errorMessage,
        status: 'failed',
        request: undefined,
        timer: undefined,
      });

      if (options.showToastOnRestrictionFail) {
        toast.danger(errorMessage);
      }

      state.runQueue();
      return null;
    }
  }

  // prepare config for file upload strategy
  const timer = new ProgressTimeout();
  const config: UploadStrategyConfig = {
    metadata: {
      ...options?.metadata,
      relativePath: file.relativePath,
      disk: options?.metadata?.disk || Disk.uploads,
      parentId: options?.metadata?.parentId || '',
    },
    chunkSize: 100 * 1024 * 1024,
    baseUrl: settings.base_url,
    onError: errorMessage => {
      const fileUpload = state.getUpload(file.id);
      if (fileUpload?.status === 'aborted') {
        timer.done();
        return;
      }

      const isValidationError = options?.restrictions && validateFile(file, options.restrictions);

      if (!isValidationError) {
        // If the browser is currently offline, don't waste retry attempts.
        // Wait until the browser comes back online to resume.
        if (typeof window !== 'undefined' && 'navigator' in window && !navigator.onLine) {
          state.updateFileUpload(file.id, {
            status: 'pending',
            errorMessage: message('Waiting for internet connection...'),
            percentage: 0,
            bytesUploaded: 0,
          });
          timer.done();

          let offlineTimer: any;
          const handleOnline = () => {
            if (offlineTimer) clearTimeout(offlineTimer);
            window.removeEventListener('online', handleOnline);
            const currentUpload = state.getUpload(file.id);
            if (currentUpload?.status === 'aborted') {
              return;
            }
            state.enqueueUpload(file.id);
          };

          const OFFLINE_TIMEOUT = 300000; // 5 minutes max wait
          offlineTimer = setTimeout(() => {
            window.removeEventListener('online', handleOnline);
            const currentUpload = state.getUpload(file.id);
            if (currentUpload?.status === 'aborted') {
              return;
            }
            state.updateFileUpload(file.id, {
              status: 'failed',
              errorMessage: message('No internet connection'),
            });
            state.runQueue();
            options?.onError?.('No internet connection', file);
            if (options.showToastOnBackendError) {
              toast.danger(message('No internet connection'));
            }
          }, OFFLINE_TIMEOUT);

          window.addEventListener('online', handleOnline);
          return;
        }

        const currentRetries = fileUpload?.autoRetries || 0;
        const MAX_AUTO_RETRIES = 3;

        if (currentRetries < MAX_AUTO_RETRIES) {
          timer.done();
          const delay = state.activeUploadsCount > 1 ? AUTO_RETRY_DELAY : 0;
          if (delay > 0) {
            setTimeout(() => {
              state.enqueueUpload(file.id, {
                autoRetries: currentRetries + 1,
              });
            }, delay);
          } else {
            state.enqueueUpload(file.id, {
              autoRetries: currentRetries + 1,
            });
          }
        } else {
          // console.error(`[Uploader] Upload failed for "${file.name}" after ${currentRetries} auto-retry attempts.`);
          state.updateFileUpload(file.id, {
            errorMessage,
            status: 'failed',
          });
          state.runQueue();
          timer.done();
          options?.onError?.(errorMessage, file);
          if (options.showToastOnBackendError && errorMessage) {
            toast.danger(errorMessage);
          }
        }
      } else {
        state.updateFileUpload(file.id, {
          errorMessage,
          status: 'failed',
        });
        state.runQueue();
        timer.done();
        options?.onError?.(errorMessage, file);
        if (options.showToastOnBackendError && errorMessage) {
          toast.danger(errorMessage);
        }
      }
    },
    onSuccess: entry => {
      state.updateFileUpload(file.id, {
        status: 'completed',
        entry,
      });
      state.runQueue();
      timer.done();
      options?.onSuccess?.(entry, file);
    },
    onProgress: ({ bytesUploaded, bytesTotal }) => {
      const percentage = (bytesUploaded / bytesTotal) * 100;
      state.updateFileUpload(file.id, {
        percentage,
        bytesUploaded,
      });
      timer.progress();
      options?.onProgress?.({ bytesUploaded, bytesTotal });
    },
  };

  // choose and create upload strategy, based on file size and settings
  try {
    const strategy = chooseUploadStrategy(file, config);
    const request = await strategy.create(file, config);

    // If upload was aborted or session was cancelled during strategy.create
    const fileUpload = state.getUpload(file.id);
    if (
      !fileUpload ||
      fileUpload.status === 'aborted' ||
      ((state as any).getUploadSessionId && (state as any).getUploadSessionId() !== sessionId)
    ) {
      request.abort();
      timer.done();
      return null;
    }

    // add handler for when upload times out (no progress for 5+ minutes)
    timer.timeoutHandler = () => {
      request.abort();

      const fileUpload = state.getUpload(file.id);
      if (fileUpload?.status === 'aborted') {
        timer.done();
        return;
      }

      if (typeof window !== 'undefined' && 'navigator' in window && !navigator.onLine) {
        state.updateFileUpload(file.id, {
          status: 'pending',
          errorMessage: message('Waiting for internet connection...'),
          percentage: 0,
          bytesUploaded: 0,
        });
        timer.done();

        let offlineTimer: any;
        const handleOnline = () => {
          if (offlineTimer) clearTimeout(offlineTimer);
          window.removeEventListener('online', handleOnline);
          const currentUpload = state.getUpload(file.id);
          if (currentUpload?.status === 'aborted') {
            return;
          }
          state.enqueueUpload(file.id);
        };

        const OFFLINE_TIMEOUT = 300000; // 5 minutes max wait
        offlineTimer = setTimeout(() => {
          window.removeEventListener('online', handleOnline);
          const currentUpload = state.getUpload(file.id);
          if (currentUpload?.status === 'aborted') {
            return;
          }
          state.updateFileUpload(file.id, {
            status: 'failed',
            errorMessage: message('No internet connection'),
          });
          state.runQueue();
          options?.onError?.('No internet connection', file);
          if (options.showToastOnBackendError) {
            toast.danger(message('No internet connection'));
          }
        }, OFFLINE_TIMEOUT);

        window.addEventListener('online', handleOnline);
        return;
      }

      const currentRetries = fileUpload?.autoRetries || 0;
      const MAX_AUTO_RETRIES = 3;

      if (currentRetries < MAX_AUTO_RETRIES) {
        timer.done();
        const delay = state.activeUploadsCount > 1 ? AUTO_RETRY_DELAY : 0;
        if (delay > 0) {
          setTimeout(() => {
            state.enqueueUpload(file.id, {
              autoRetries: currentRetries + 1,
            });
          }, delay);
        } else {
          state.enqueueUpload(file.id, {
            autoRetries: currentRetries + 1,
          });
        }
      } else {
        // console.error(`[Uploader] Upload timed out for "${file.name}" after ${currentRetries} auto-retry attempts. Marking as failed.`);
        state.updateFileUpload(file.id, {
          status: 'failed',
          errorMessage: message('Upload timed out'),
        });
        state.runQueue();
        timer.done();
      }
    };

    state.updateFileUpload(file.id, {
      status: 'inProgress',
      request,
    });
    request.start();

    return request;
  } catch (err: any) {
    // console.error(`[Uploader] Failed to initiate upload for "${file.name}":`, err);
    config.onError?.(err?.message || 'Could not initiate upload', file);
    return null;
  }
}

const OneMB = 1024 * 1024;
const FourMB = 2 * OneMB;
const HundredMB = 100 * OneMB;

const chooseUploadStrategy = (
  file: UploadedFile,
  config: UploadStrategyConfig,
) => {
  const settings = getBootstrapData().settings;
  const disk = config.metadata?.disk || Disk.uploads;
  const driver =
    disk === Disk.uploads
      ? settings.uploads.uploads_driver
      : settings.uploads.public_driver;

  if (driver?.endsWith('s3') && settings.uploads.s3_direct_upload) {
    return file.size >= HundredMB ? S3MultipartUpload : S3Upload;
  } else {
    // 4MB = Axios, otherwise Tus
    return file.size >= FourMB && !settings.uploads.disable_tus
      ? TusUpload
      : AxiosUpload;
  }
};
